import React from 'react';
import { Link } from 'react-router-dom';

const USRetirementPlannerInfoPage: React.FC = () => {
  return (
    <div className="retire-info-page">
      <div className="hero-section retirement">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">US Retirement Planner - 401k, IRA & Social Security</h1>
            <p className="hero-description">Plan retirement with RMDs, Social Security optimization, Roth conversions, IRMAA brackets, and Monte Carlo simulation. Get clear guidance and actionable outputs.</p>
            <div className="cta-buttons">
              <Link to="/us-retirement" className="btn btn-primary btn-large">Open Planner</Link>
              <Link to="/" className="btn btn-secondary btn-large">Back to Home</Link>
            </div>
          </div>
        </div>
      </div>

      <section className="features-section">
        <div className="container">
          <h2>Key Features</h2>
          <div className="features-grid">
            <div className="feature-card"><div className="feature-icon">📊</div><h3>Monte Carlo Simulation</h3><p>10,000 scenarios showing range of outcomes, not just averages.</p></div>
            <div className="feature-card"><div className="feature-icon">🏛️</div><h3>Social Security Optimization</h3><p>Find optimal claiming age (62, 67, or 70) based on your situation.</p></div>
            <div className="feature-card"><div className="feature-icon">💰</div><h3>401k/IRA/Roth Strategy</h3><p>Tax-efficient withdrawal ordering and Roth conversion planning.</p></div>
            <div className="feature-card"><div className="feature-icon">🏥</div><h3>Medicare IRMAA</h3><p>Avoid the Medicare high-income surcharge with smart income planning.</p></div>
          </div>
        </div>
      </section>

      <section className="pricing-section">
        <div className="container">
          <h2>Features by Tier</h2>
          <div className="pricing-grid">
            <div className="pricing-card free"><h3>Free</h3><div className="price">$0</div><ul><li>Full Monte Carlo simulation</li><li>Social Security optimization</li><li>Withdrawal strategy comparison</li><li>Year-by-year projections</li></ul><Link to="/us-retirement" className="btn btn-primary">Start Free</Link></div>
            <div className="pricing-card account"><h3>Account</h3><div className="price">Free</div><ul><li>Save scenarios</li><li>Load/update scenarios</li></ul><Link to="/account" className="btn btn-secondary">Create Account</Link></div>
            <div className="pricing-card supporter"><h3>Supporters Club</h3><div className="price">$10/year</div><ul><li>Everything in Free & Account</li><li>Advanced optimizations</li><li>Early access to features</li></ul><Link to="/account" className="btn btn-premium">Join Supporters Club</Link></div>
          </div>
        </div>
      </section>

      <section className="guide-section">
        <div className="container">
          <h2>Complete Guide: Social Security, 401k/IRA & Retirement Planning in the USA</h2>

          <div className="guide-content">
            <h3>The $200,000 Decision: When Should You Claim Social Security?</h3>
            <p>When you start Social Security can make a $150,000-$300,000 difference in lifetime benefits. Most Americans claim at 62 or Full Retirement Age by default, but that's often the wrong choice. Here's the math:</p>

            <h4>Social Security at Age 62 vs 67 vs 70: Real Numbers</h4>
            <p><strong>Assumptions:</strong> Your Primary Insurance Amount (PIA) is $2,500/month at Full Retirement Age (67)</p>

            <h5>Option 1: Claim at Age 62</h5>
            <ul>
              <li>Reduced by 30% (permanently) = $1,750/month = $21,000/year</li>
              <li>From age 62-85 (23 years): $21,000 × 23 = $483,000 total</li>
              <li><strong>Break-even vs age 67:</strong> Age 78</li>
            </ul>

            <h5>Option 2: Claim at Full Retirement Age (67)</h5>
            <ul>
              <li>Full amount: $2,500/month = $30,000/year</li>
              <li>From age 67-85 (18 years): $30,000 × 18 = $540,000 total</li>
            </ul>

            <h5>Option 3: Delay to Age 70</h5>
            <ul>
              <li>Increased by 24% (8% per year for 3 years) = $3,100/month = $37,200/year</li>
              <li>From age 70-85 (15 years): $37,200 × 15 = $558,000 total</li>
              <li><strong>Break-even vs age 67:</strong> Age 82</li>
            </ul>

            <p><strong>Verdict if you live to 85:</strong> Delaying to 70 wins by $18,000+. Claiming at 62 loses $57,000 vs claiming at 67.</p>
            <p><strong>BUT:</strong> If you die at 75, claiming at 62 wins by $123,000. This is why your health, family longevity, and income needs TODAY matter enormously.</p>

            <h3>When Claiming Social Security at 62 Makes Sense</h3>

            <h4>Scenario 1: Health Issues or Poor Family Longevity</h4>
            <p>If you have serious health conditions or family history shows average lifespan under 78, claiming early maximizes your lifetime benefits. Getting $21k/year now beats $37k/year that you might not live to collect.</p>

            <h4>Scenario 2: You're Unemployed Age 62-66 with Limited Savings</h4>
            <p>If you lose your job at 62 and have limited 401k savings, Social Security provides immediate income to avoid depleting retirement accounts or going into debt.</p>

            <h4>Scenario 3: Your Spouse Has Much Higher Benefits</h4>
            <p>If your spouse has significantly higher lifetime benefits, they should delay to 70 while you claim early. This ensures maximum survivor benefits (the higher earner's benefit) while getting some income now.</p>

            <h3>When Delaying to 70 Makes Sense</h3>

            <h4>Scenario 1: You're Still Working Age 67-70</h4>
            <p>If you're earning $80k+/year working age 67-70, adding Social Security income just gets taxed heavily. Better to delay, live off work income, and start at 70. The 24% increase is like getting a guaranteed 8% annual return—better than bonds, risk-free.</p>

            <h4>Scenario 2: Strong Family Longevity (Expect to Live to 85+)</h4>
            <p>If your parents/grandparents lived to 85-95 and you're in good health, delaying maximizes lifetime benefits. The break-even is age 82—every year after that is pure profit.</p>

            <h4>Scenario 3: You Have Sufficient 401k/IRA to Bridge to Age 70</h4>
            <p>If you have $500k+ in retirement accounts, you can draw down these accounts age 67-70 while delaying Social Security. Bonus: You can do Roth conversions during this low-income window.</p>

            <h3>Medicare IRMAA: The Income Surcharge Trap</h3>
            <p>Medicare Part B and D premiums increase based on your Modified Adjusted Gross Income (MAGI) from two years prior. The Income-Related Monthly Adjustment Amount (IRMAA) brackets for 2024:</p>

            <h4>2024 IRMAA Brackets (Single Filers)</h4>
            <ul>
              <li><strong>$103,000 or less:</strong> Standard premium ($174.70/month)</li>
              <li><strong>$103,001-$129,000:</strong> +$69.90/month</li>
              <li><strong>$129,001-$161,000:</strong> +$174.70/month</li>
              <li><strong>$161,001-$193,000:</strong> +$279.50/month</li>
              <li><strong>$193,001-$500,000:</strong> +$384.30/month</li>
              <li><strong>Over $500,000:</strong> +$419.30/month</li>
            </ul>

            <h4>Strategies to Avoid IRMAA</h4>

            <h5>1. Manage Income Two Years Before Medicare (Age 63-64)</h5>
            <p>IRMAA uses income from 2 years prior. If you turn 65 in 2026, your 2024 income determines your 2026 premiums. Avoid large 401k withdrawals or Roth conversions in those key years.</p>

            <h5>2. Roth Conversions Before Age 63</h5>
            <p>Do aggressive Roth conversions age 60-62, before the IRMAA lookback period begins. Pay taxes now at lower rates to avoid IRMAA surcharges later.</p>

            <h5>3. Use Roth IRA Withdrawals in High-Income Years</h5>
            <p>Roth withdrawals don't count as MAGI. If you need extra income in retirement but are near an IRMAA threshold, withdraw from Roth instead of Traditional accounts.</p>

            <h3>Required Minimum Distributions (RMDs): The IRS Wants Its Money</h3>
            <p>At age 73 (increased from 72 by SECURE 2.0), you MUST start withdrawing from Traditional 401k/IRA accounts. RMD percentages by age:</p>
            <ul>
              <li><strong>Age 73:</strong> 3.77% minimum (1/26.5 of balance)</li>
              <li><strong>Age 75:</strong> 4.07%</li>
              <li><strong>Age 80:</strong> 5.35%</li>
              <li><strong>Age 85:</strong> 7.09%</li>
              <li><strong>Age 90:</strong> 9.80%</li>
            </ul>

            <p><strong>Example:</strong> If you have $1M in Traditional IRA at age 73, you must withdraw $37,700 (3.77%). By age 85, if the balance is still $1M, you must withdraw $70,900. This is fully taxable income.</p>

            <h4>The RMD Problem</h4>
            <p>Large Traditional account balances lead to forced high-income years, which cause:</p>
            <ul>
              <li>Higher marginal tax brackets (22% → 32% or higher)</li>
              <li>IRMAA surcharges on Medicare premiums</li>
              <li>85% of Social Security becoming taxable</li>
              <li>Net Investment Income Tax (NIIT) of 3.8% on investment income</li>
            </ul>

            <h3>Roth Conversion Strategy: The Tax Arbitrage Opportunity</h3>
            <p>Converting Traditional IRA to Roth IRA triggers taxes NOW but creates tax-free income LATER. The key is doing conversions in low-income years.</p>

            <h4>The Roth Conversion Window: Ages 60-72</h4>
            <p>After you stop working (age 60-65) but before RMDs begin (age 73), you may be in an unusually low tax bracket. This is the ideal time to convert.</p>

            <h4>Fill the Bracket Strategy</h4>
            <p><strong>Example:</strong> Single filer, age 62, no W-2 income</p>
            <ul>
              <li>Standard deduction: $14,600 (2024)</li>
              <li>10% bracket: $0-$11,600 = $11,600 at 10% = $1,160 tax</li>
              <li>12% bracket: $11,600-$47,150 = $35,550 at 12% = $4,266 tax</li>
              <li>22% bracket: $47,150-$100,525 = $53,375 at 22% = $11,743 tax</li>
              <li><strong>Convert up to $115,125 (standard deduction + top of 22% bracket)</strong></li>
              <li>Total tax on conversion: $17,169 (effective rate: 14.9%)</li>
            </ul>

            <p>Without conversion, this $115k stays in Traditional IRA, grows to $200k+ by age 80, and gets withdrawn at 24-32% rates plus IRMAA. Conversion saves $15,000+ in taxes.</p>

            <h4>Avoid IRMAA Strategy</h4>
            <p>Stay below IRMAA thresholds during conversion years. For singles, stay under $103,000 MAGI. Convert smaller amounts over more years if needed.</p>

            <h3>Tax-Efficient Withdrawal Sequencing</h3>

            <h4>Traditional Order (Simple)</h4>
            <ol>
              <li><strong>Taxable brokerage accounts first</strong> - Only capital gains taxed (15-20%), not full withdrawal</li>
              <li><strong>Traditional 401k/IRA second</strong> - Fully taxable at ordinary income rates</li>
              <li><strong>Roth IRA last</strong> - Tax-free, let it grow as long as possible</li>
            </ol>

            <h4>Tax-Bracket Management Order (Advanced)</h4>
            <p>Blend withdrawals to optimize your tax bracket each year:</p>
            <ol>
              <li>Withdraw enough from Traditional to fill low brackets (10%, 12%)</li>
              <li>Take long-term capital gains at 0% rate if income allows ($47,025 single, $94,050 married)</li>
              <li>Use Roth for additional income to avoid jumping to higher brackets</li>
              <li>Do Roth conversions if you have "room" in your bracket</li>
            </ol>

            <h3>Social Security Taxation: The 85% Trap</h3>
            <p>Up to 85% of Social Security benefits can be taxed based on your "combined income" (AGI + nontaxable interest + 50% of SS benefits):</p>
            <ul>
              <li><strong>Single, under $25,000:</strong> 0% taxable</li>
              <li><strong>Single, $25,000-$34,000:</strong> Up to 50% taxable</li>
              <li><strong>Single, over $34,000:</strong> Up to 85% taxable</li>
              <li><strong>Married, under $32,000:</strong> 0% taxable</li>
              <li><strong>Married, $32,000-$44,000:</strong> Up to 50% taxable</li>
              <li><strong>Married, over $44,000:</strong> Up to 85% taxable</li>
            </ul>

            <p><strong>Strategy:</strong> Use Roth withdrawals to avoid pushing combined income over these thresholds. Every $1 of Traditional IRA withdrawal can trigger $0.85 of Social Security becoming taxable—effectively a 85% penalty on that withdrawal.</p>

            <h3>Real Retirement Scenarios with Numbers</h3>

            <h4>Scenario 1: Single, $45k Social Security, $600k 401k, Age 65</h4>
            <p><strong>Decision:</strong> When to claim SS? How to handle RMDs?</p>

            <p><strong>If claim SS at 65 (reduced from 67):</strong></p>
            <ul>
              <li>Social Security: $38,250/year (15% reduction)</li>
              <li>Need: $60k/year spending → Gap: $21,750 from 401k</li>
              <li>At age 73: $600k grows to ~$850k, RMD = $32,050</li>
              <li>Combined income: $38k SS + $32k RMD = $70k → 85% of SS taxable</li>
            </ul>

            <p><strong>Better strategy:</strong></p>
            <ol>
              <li>Delay Social Security to age 70 ($56,070/year)</li>
              <li>Live on 401k withdrawals age 65-70 ($60k/year = $300k total)</li>
              <li>Do Roth conversions during these years (low income)</li>
              <li>At 70: SS covers most needs, minimal 401k withdrawals needed</li>
              <li>At 73: 401k balance lower ($300k), RMD only $11,310</li>
              <li>Lower combined income = less SS taxation, no IRMAA</li>
            </ol>

            <h4>Scenario 2: Married, $85k Combined SS at 70, $1.5M 401k, Ages 62 & 64</h4>
            <p><strong>Decision:</strong> How to manage massive 401k without huge tax bills?</p>

            <p><strong>Problem:</strong></p>
            <ul>
              <li>At age 73, RMD on $1.5M (grown to $2.1M) = $79,170</li>
              <li>Plus SS: $85k → Total income: $164k</li>
              <li>Tax bracket: 24%+, IRMAA surcharges, 85% SS taxable</li>
            </ul>

            <p><strong>Solution: Aggressive Roth Conversions Age 62-72</strong></p>
            <ol>
              <li>Retire at 62, convert $100k/year for 10 years to Roth</li>
              <li>Pay ~$18k/year in conversion taxes (18% effective rate)</li>
              <li>Total taxes: $180k, but converts $1M to Roth</li>
              <li>At 73: Traditional balance ~$700k, RMD = $26,390</li>
              <li>Combined income: $111k → Just above first IRMAA threshold</li>
              <li>Plus $1M+ Roth growing tax-free for later</li>
            </ol>

            <p><strong>Savings:</strong> $50k+ in lifetime taxes, no IRMAA penalties, estate passes Roth to heirs tax-free.</p>

            <h3>State Tax Differences: Where You Retire Matters</h3>

            <p><strong>Retirement income: $75,000 (SS + 401k withdrawals)</strong></p>

            <h4>No State Income Tax States</h4>
            <p>Alaska, Florida, Nevada, South Dakota, Tennessee, Texas, Washington, Wyoming</p>
            <ul>
              <li>Federal tax only: ~$8,500</li>
              <li><strong>After-tax income: $66,500</strong></li>
            </ul>

            <h4>States That Don't Tax Retirement Income</h4>
            <p>Illinois, Mississippi, Pennsylvania (don't tax 401k/IRA withdrawals)</p>
            <ul>
              <li>Federal tax: ~$8,500</li>
              <li>State tax: Minimal or none on retirement income</li>
              <li><strong>After-tax income: ~$66,000</strong></li>
            </ul>

            <h4>High-Tax States</h4>
            <p>California, New York, New Jersey</p>
            <ul>
              <li>Federal tax: ~$8,500</li>
              <li>State tax: $4,000-6,000</li>
              <li><strong>After-tax income: $60,500-62,000</strong></li>
            </ul>

            <p><strong>Over 20 years of retirement:</strong> Moving from California to Florida saves $80,000-120,000 in state taxes on $75k income. Factor in cost of living differences—Florida has no state income tax but higher property insurance; Texas has high property taxes.</p>

            <h3>Common Retirement Planning Mistakes</h3>

            <h4>Mistake 1: Not Doing Roth Conversions in Low-Income Years</h4>
            <p>The window between retirement (age 60-65) and RMDs (age 73) is precious. Every year you don't convert in a low bracket is a missed opportunity. Don't let your Traditional IRA grow into an RMD time bomb.</p>

            <h4>Mistake 2: Claiming Social Security Too Early</h4>
            <p>62 is the most common claiming age, but it's often wrong. If you have savings to bridge 5-8 years, delaying to 70 is like buying an 8% inflation-adjusted annuity—better than any product you can buy.</p>

            <h4>Mistake 3: Ignoring IRMAA</h4>
            <p>A $1 increase in income over an IRMAA threshold costs $1,000+/year in Medicare surcharges. Plan conversions and withdrawals to stay just under thresholds. Look two years ahead.</p>

            <h4>Mistake 4: All Traditional, No Roth</h4>
            <p>Having 100% in Traditional accounts means 100% of withdrawals are taxable. Even if you're in a high bracket now, some Roth contributions/conversions provide tax diversification and flexibility in retirement.</p>
          </div>
        </div>
      </section>

      <section className="faq-section">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>When should I claim Social Security?</h3>
              <p>It depends on your health, income needs, and longevity expectations. Claim at 62 if: You need income immediately, have health issues, or family history suggests shorter lifespan (under 78). Claim at Full Retirement Age (67) if: Standard retirement, average health. Delay to 70 if: You're still working (so SS would just be taxed heavily), have excellent health/family longevity, or sufficient savings to bridge. Break-even: Claiming at 62 vs 67 breaks even at age 78. Claiming at 67 vs 70 breaks even at age 82.</p>
            </div>
            <div className="faq-item">
              <h3>What is IRMAA and how do I avoid it?</h3>
              <p>IRMAA (Income-Related Monthly Adjustment Amount) is a surcharge on Medicare premiums for high earners. It uses income from 2 years prior. To avoid: (1) Manage income in years before Medicare eligibility (age 63-64). (2) Do Roth conversions before age 63. (3) Use Roth withdrawals instead of Traditional when near thresholds. Every $10k over a threshold costs ~$1,200/year in surcharges.</p>
            </div>
            <div className="faq-item">
              <h3>Should I do Roth conversions?</h3>
              <p>Probably yes, especially if: (1) You're retired but not yet taking Social Security (low income years). (2) You're in a lower tax bracket now than you expect in retirement. (3) You have a large Traditional IRA that will create RMD problems at 73. (4) You want to leave tax-free money to heirs. Convert up to the top of your current bracket ("fill the bracket" strategy) each year.</p>
            </div>
            <div className="faq-item">
              <h3>When do RMDs start and how much?</h3>
              <p>Required Minimum Distributions start at age 73 (thanks to SECURE 2.0). The first RMD is ~3.77% of your Traditional IRA/401k balance. It increases each year: ~5.35% at 80, ~7.09% at 85, ~9.80% at 90. On a $1M balance at 73, you must withdraw $37,700, fully taxable. Roth IRAs have no RMDs (a key advantage).</p>
            </div>
            <div className="faq-item">
              <h3>How is Social Security taxed?</h3>
              <p>Up to 85% of Social Security can be taxable depending on your "combined income" (AGI + nontaxable interest + half of SS). For singles: 0% taxable under $25k combined income, up to 85% taxable over $34k. For married: 0% taxable under $32k, up to 85% taxable over $44k. Strategy: Use Roth withdrawals to stay under these thresholds.</p>
            </div>
            <div className="faq-item">
              <h3>What's the 4% withdrawal rule?</h3>
              <p>The 4% rule suggests you can safely withdraw 4% of your portfolio in year 1, then adjust for inflation, with 95% confidence of lasting 30 years. So $1M = $40k/year safe withdrawal. However, this is a guideline, not a guarantee. Our Monte Carlo simulation runs 10,000 scenarios to show your actual success probability based on your specific situation.</p>
            </div>
            <div className="faq-item">
              <h3>Traditional 401k or Roth 401k?</h3>
              <p>Traditional: Tax deduction now, pay taxes on withdrawals later. Roth: No deduction now, tax-free withdrawals later. Choose Traditional if: You're in a high bracket now and expect lower bracket in retirement, need the tax deduction now. Choose Roth if: You're in a low bracket now, expect higher bracket in retirement, or want tax diversification. Many people benefit from having both.</p>
            </div>
            <div className="faq-item">
              <h3>How do I account for healthcare costs?</h3>
              <p>Medicare covers much, but not all. Budget for: Part B premium ($174.70+/month), Part D drug coverage ($30-100/month), Medigap supplemental insurance ($100-300/month), dental/vision (not covered), and long-term care (potentially $6,000-10,000/month for nursing home). HSA is valuable—save in it before 65, use it tax-free for medical expenses in retirement.</p>
            </div>
            <div className="faq-item">
              <h3>What about state taxes in retirement?</h3>
              <p>9 states have no income tax: AK, FL, NV, NH (dividends only), SD, TN, TX, WA, WY. Several states don't tax retirement income specifically: IL, MS, PA. High-tax states like CA, NY, NJ will tax 401k/IRA withdrawals at 6-10%+. Consider state taxes when choosing where to retire—it can mean $50k-100k+ over 20 years.</p>
            </div>
            <div className="faq-item">
              <h3>How much do I need to retire?</h3>
              <p>Rule of thumb: 25x your annual expenses (the inverse of the 4% rule). If you need $60k/year, target $1.5M saved. But factor in Social Security—if SS provides $30k/year, you only need to cover $30k from savings = $750k. Our calculator models your specific situation including Social Security, pensions, and spending to show exactly what you need.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <h2>Start Planning</h2>
          <p>Optimize your Social Security, 401k, and IRA for maximum retirement income.</p>
          <div className="cta-buttons">
            <Link to="/us-retirement" className="btn btn-primary btn-large">Open US Retirement Planner</Link>
            <Link to="/account" className="btn btn-secondary btn-large">Create Free Account</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default USRetirementPlannerInfoPage;
